import { useEffect, useState } from "react";
import { Globe, Save, Loader2, Database } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { WG_COLORS } from "@/components/dashboard/WorkgroupBar";

const WorkgroupCard = ({ wg, isCurrent, onSaved }) => {
  const [name, setName] = useState(wg.name);
  const [domains, setDomains] = useState((wg.domains || []).join("\n"));
  const [saving, setSaving] = useState(false);
  const dirty = name !== wg.name || domains !== (wg.domains || []).join("\n");

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/workgroups/${wg.slug}`, {
        name,
        domains: domains.split(/[\n,]/).map((d) => d.trim()).filter(Boolean),
      });
      onSaved(res.data);
      toast.success(`${res.data.name} saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save workgroup");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`space-y-3 rounded-lg border p-4 ${isCurrent ? "border-white/30 bg-white/[0.05]" : "border-white/10 bg-white/[0.02]"}`}
      data-testid={`workgroup-card-${wg.slug}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={`h-3 w-3 rounded-full ${WG_COLORS[wg.slug]?.bar || ""}`} />
          <p className="text-sm font-semibold text-white">{wg.name}</p>
          {isCurrent && (
            <span className="rounded bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-300" data-testid={`workgroup-current-${wg.slug}`}>
              you are here
            </span>
          )}
        </div>
        <span className="flex items-center gap-1 font-mono text-[11px] text-white/40">
          <Database className="h-3 w-3" /> {wg.db}
        </span>
      </div>
      <div>
        <Label htmlFor={`wg-name-${wg.slug}`} className="text-xs text-white/50">Display name</Label>
        <Input id={`wg-name-${wg.slug}`} value={name} onChange={(e) => setName(e.target.value)}
          data-testid={`workgroup-name-${wg.slug}`} className="mt-1 border-white/15 bg-white/5 text-white" />
      </div>
      <div>
        <Label htmlFor={`wg-domains-${wg.slug}`} className="text-xs text-white/50">
          Domains (one per line) — anyone loading the portal from these addresses lands in this workgroup
        </Label>
        <textarea
          id={`wg-domains-${wg.slug}`}
          value={domains}
          onChange={(e) => setDomains(e.target.value)}
          rows={2}
          placeholder={"portal.example.com"}
          data-testid={`workgroup-domains-${wg.slug}`}
          className="mt-1 w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 font-mono text-xs text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#1a3fd1]"
        />
      </div>
      <div className="flex justify-end">
        <Button size="sm" onClick={save} disabled={!dirty || saving} data-testid={`workgroup-save-${wg.slug}`}
          className="bg-[#1a3fd1] text-white hover:bg-[#1a3fd1]/85">
          {saving ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-2 h-3.5 w-3.5" />} Save
        </Button>
      </div>
    </div>
  );
};

export const WorkgroupsPanel = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/workgroups").then((r) => setData(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Could not load workgroups"));
  }, []);

  if (!data) return <p className="text-xs text-white/40">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="workgroups-panel">
      <div className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
        <Globe className="mt-0.5 h-4 w-4 text-[#4f6fe8]" />
        <div>
          <p className="text-sm font-semibold text-white">Workgroups</p>
          <p className="text-xs text-white/40">
            Each workgroup is a completely separate database — users, customers, projects, timesheets, files,
            branding and every admin setting. The workgroup is chosen by the domain the portal is loaded from;
            unknown addresses fall back to Elite. New domains get an HTTPS certificate automatically once they
            point at this server. Use the bar at the top of the dashboard to switch.
          </p>
        </div>
      </div>
      {data.workgroups.map((wg) => (
        <WorkgroupCard
          key={wg.slug}
          wg={wg}
          isCurrent={wg.slug === data.current}
          onSaved={(saved) => setData((d) => ({ ...d, workgroups: d.workgroups.map((w) => (w.slug === saved.slug ? { ...w, ...saved } : w)) }))}
        />
      ))}
    </div>
  );
};

export default WorkgroupsPanel;
